import DatasetMultiSelect from "components/basic/dropdowns/dataset/DatasetMultiSelect";
import TimeUnitDropdown from "components/basic/dropdowns/time-unit-dropdown/TimeUnitDropdown";
import { DashboardEditorProps } from "components/ComponentType";
import { LoadingConfig } from "components/dashboard/charts/interfaces";
import { defaultLoadConf } from "components/dashboard/data-input-output-table/defaultConfigs";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown, DropdownChangeParams } from "primereact/dropdown";
import { InputNumber, InputNumberValueChangeParams } from "primereact/inputnumber";
import React, { useCallback, useEffect, useState } from "react";
import { T } from "services/lang/LanguageService";
import { ChronoUnitLegacy } from "utils/time-utils";
import DatasetDataInputTableDatasetsEditor from "./DatasetDataInputTableDatasetsEditor";
import { DatasetDataInputTableProps } from "../DatasetDataInputTable";

export const defaultDatasetDataInputTableConfig: DatasetDataInputTableProps = {
  tableHeight: 400,
  loadConf: defaultLoadConf,
  stepSize: 1,
  stepUnit: ChronoUnitLegacy.HOURS,
  sort: "ASC",
  datasets: [],
};

const sortOptions = [
  { label: T("word.ascending"), value: "ASC" },
  { label: T("word.descending"), value: "DESC" },
];

const DatasetDataInputTableEditor = ({
  config,
  onConfigChange,
}: DashboardEditorProps<DatasetDataInputTableProps>) => {
  const conf =
    config && Object.keys(config).length !== 0 ? config : defaultDatasetDataInputTableConfig;

  const currentDatasets = conf.datasets.map(dataset => dataset.identifier);

  const [currentConfig, setCurrentConfig] = useState<DatasetDataInputTableProps>(conf);
  const [selectedDatasets, setSelectedDatasets] = useState<string[]>(currentDatasets);
  const [datasetsEditorDialogVisibility, setDatasetsEditorDialogVisibility] =
    useState<boolean>(false);

  useEffect(() => {
    if (currentConfig.datasets) {
      onConfigChange(currentConfig);
    }
  }, [currentConfig]);

  const handleLoadConfDetailsChange = useCallback(
    (e: InputNumberValueChangeParams | DropdownChangeParams) => {
      const key = e.target.id as keyof LoadingConfig;
      const value = Number.isNaN(e.target.value)
        ? e.target.value.toString().toUpperCase()
        : e.target.value;
      setCurrentConfig(prev => ({
        ...prev,
        loadConf: { ...prev.loadConf, [key]: value.toString().toUpperCase() },
      }));
    },
    [currentConfig.loadConf]
  );

  const handleDatasetChange = useCallback(
    (selectedIdentifiers: string[]): void => {
      const selectedDatasetsArray = selectedIdentifiers.map(identifier => {
        const oldDataset = currentConfig.datasets?.find(
          dataset => dataset.identifier === identifier
        );
        return oldDataset ?? { identifier };
      });

      setCurrentConfig(prev => ({ ...prev, datasets: selectedDatasetsArray }));
      setSelectedDatasets(selectedIdentifiers);
    },
    [currentConfig.datasets]
  );

  const handleEditDatasets = useCallback(() => {
    setDatasetsEditorDialogVisibility(true);
  }, []);

  const handleDatasetDatainputTableDialogFooter = () => {
    return (
      <Button
        label={T("action.close")}
        type="button"
        className="p-button-danger align-self-end"
        onClick={() => setDatasetsEditorDialogVisibility(false)}
      />
    );
  };

  return (
    <div className="grid p-fluid flex flex-column">
      <div className="col-12 flex flex-column mb-3">
        <label htmlFor="identifiers">{T("word.identifiers")}</label>
        <div className="flex gap-2">
          <DatasetMultiSelect value={selectedDatasets} onChange={handleDatasetChange} />
          {selectedDatasets.length > 0 && (
            <>
              <Button
                icon="pi pi-pencil"
                type="button"
                className="p-button-secondary"
                tooltip={T("action.edit-lines")}
                onClick={handleEditDatasets}
              />
              <Dialog
                onHide={() => setDatasetsEditorDialogVisibility(false)}
                visible={datasetsEditorDialogVisibility}
                header={T("action.edit-line-style")}
                footer={handleDatasetDatainputTableDialogFooter}
                style={{ width: "min(100vw, 1000px)", height: "400px" }}
              >
                <DatasetDataInputTableDatasetsEditor
                  currentConfig={currentConfig}
                  setCurrentConfig={setCurrentConfig}
                />
              </Dialog>
            </>
          )}
        </div>
      </div>
      <div className="p-12 flex flex-column gap-3">
        <div className="flex justify-content-between gap-3">
          <div className="w-4 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="lag">{T("load-conf.lag")}</label>
            <div className="p-inputgroup">
              <InputNumber
                id="lag"
                value={currentConfig.loadConf.lag}
                onValueChange={handleLoadConfDetailsChange}
              />
              <TimeUnitDropdown
                id="lagUnit"
                className="p-inputgroup-addon p-0"
                value={currentConfig.loadConf.lagUnit}
                onChange={handleLoadConfDetailsChange}
              />
            </div>
          </div>
          <div className="w-4 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="lead">{T("load-conf.lead")}</label>
            <div className="p-inputgroup">
              <InputNumber
                id="lead"
                value={currentConfig.loadConf.lead}
                onValueChange={handleLoadConfDetailsChange}
              />
              <TimeUnitDropdown
                id="leadUnit"
                className="p-inputgroup-addon p-0"
                value={currentConfig.loadConf.leadUnit}
                onChange={handleLoadConfDetailsChange}
              />
            </div>
          </div>
          <div className="w-4 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="alignUnit">{T("load-conf.align-unit")}</label>
            <TimeUnitDropdown
              id="alignUnit"
              className="w-full"
              value={currentConfig.loadConf.alignUnit || ""}
              onChange={handleLoadConfDetailsChange}
            />
          </div>
        </div>
        <div className="flex flex-row justify-content-start gap-3">
          <div className="w-4 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="stepUnit">{T("load-conf.steps")}</label>
            <div className="p-inputgroup">
              <InputNumber
                id="step"
                value={currentConfig.stepSize}
                onValueChange={e => {
                  setCurrentConfig(prev => ({ ...prev, stepSize: e.value ?? 1 }));
                }}
              />
              <TimeUnitDropdown
                id="stepUnit"
                className="p-inputgroup-addon p-0"
                value={currentConfig.stepUnit || ""}
                onChange={e => {
                  setCurrentConfig(prev => ({
                    ...prev,
                    stepUnit: e.value.toString().toUpperCase(),
                  }));
                }}
              />
            </div>
          </div>
          <div className="w-4 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="sort">Sort type</label>
            <Dropdown
              id="sort"
              value={currentConfig.sort}
              className="w-full"
              options={sortOptions}
              onChange={e => {
                setCurrentConfig(prev => ({
                  ...prev,
                  sort: e.value.toString().toUpperCase(),
                }));
              }}
            />
          </div>
          <div className="w-4 gap-2 flex flex-column justify-content-center align-items-center" />
        </div>
      </div>
    </div>
  );
};

export default DatasetDataInputTableEditor;
